import type { PrintMethod, PrintSettings } from '@renderer/stores/printStore'
import { usePrintStore } from '@renderer/stores/printStore'

/** Synthetic preset id for the Dreambooth Recommended dropdown option. */
export const DREAMBOOTH_RECOMMENDED_PRESET_ID = 'dreambooth-recommended'

export type RecommendationConfidence = 'high' | 'medium' | 'low'

export interface PrinterRecommendation {
  id: string
  label: string
  /** Case-insensitive substring match against the raw Windows printer name. */
  matchers: string[]
  /** Higher priority wins when multiple matchers hit. */
  priority: number
  confidence: RecommendationConfidence
  /** When false, show in dropdown only — do not auto-apply on first select. */
  autoApply: boolean
  settings: PrintSettings
  printMethod: PrintMethod
}

const STOCK_PAGE_SIZE = { width: 156100, height: 105000 }

/** Strip Windows duplicate suffixes and normalize whitespace for comparison. */
export function normalizePrinterName(name: string): string {
  return name
    .replace(/\s*\((?:Copy|Salin)\s*\d+\)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function rec(
  id: string,
  label: string,
  matchers: string[],
  priority: number,
  confidence: RecommendationConfidence,
  autoApply: boolean,
  settings: PrintSettings,
  printMethod: PrintMethod
): PrinterRecommendation {
  return { id, label, matchers, priority, confidence, autoApply, settings, printMethod }
}

const defaultMargins = (marginType: 'default' | 'none' | 'printableArea' = 'default') => ({
  margins: { marginType }
})

const dpi = (horizontal: number, vertical: number = horizontal) => ({ dpi: { horizontal, vertical } })

/**
 * Curated from fleet telemetry — see docs/printer-setup-recommendations.md.
 * Sorted by priority at lookup time; list order is not significant.
 */
export const PRINTER_RECOMMENDATIONS: PrinterRecommendation[] = [
  rec(
    'ds-rx1-no-2inch-cut',
    'DS-RX1 (No 2inch Cut)',
    ['no 2inch cut'],
    220,
    'high',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 104987, height: 156125 },
      ...defaultMargins('default'),
      ...dpi(600),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'iron-golem-2inch-cut',
    'IRONGOLEM DS-RX1 (2inch cut)',
    ['iron-golem', 'iron golem'],
    210,
    'high',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 104987, height: 156125 },
      ...defaultMargins('default'),
      ...dpi(600),
      scaleFactor: 88,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'ds-rx1-strip',
    'DS-RX1 (strip)',
    ['(strip)'],
    205,
    'medium',
    true,
    {
      orientation: 'landscape',
      pageSize: { width: 156100, height: 105000 },
      ...defaultMargins('default'),
      ...dpi(300),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'ds-rx1',
    'DS-RX1',
    ['ds-rx1', 'ds rx1'],
    100,
    'high',
    true,
    {
      orientation: 'landscape',
      pageSize: { width: 156125, height: 104987 },
      ...defaultMargins('default'),
      ...dpi(300),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'dp-ds620',
    'DP-DS620',
    ['dp-ds620', 'ds620'],
    100,
    'high',
    true,
    {
      orientation: 'landscape',
      pageSize: { width: 156100, height: 105000 },
      ...defaultMargins('default'),
      ...dpi(600),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'epson-l8050',
    'EPSON L8050 Series',
    ['l8050', 'epsone98eed', 'epsonec054b'],
    100,
    'high',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 101600, height: 152400 },
      ...defaultMargins('default'),
      ...dpi(720),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'sumatra'
  ),
  rec(
    'epson-sl-d500',
    'EPSON SL-D500 Series',
    ['sl-d500', 'sld500'],
    100,
    'medium',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 102000, height: 152000 },
      ...defaultMargins('none'),
      ...dpi(720),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'sumatra'
  ),
  rec(
    'kodak-605',
    'KODAK 605 Photo Printer',
    ['kodak 605', '605 photo'],
    100,
    'medium',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 101600, height: 152400 },
      ...defaultMargins('default'),
      ...dpi(600),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'kodak-68xx',
    'KODAK Photo Printer 6800 / 6850',
    ['kodak 6800', 'kodak 6850', '6800 printer', '6850 printer'],
    100,
    'low',
    // Dropdown-only: no booth has yet produced a verified-good print on a 68XX,
    // so this must not overwrite an operator's working setup. The page size
    // below is a 4x6 starting point — for 2x6 strips pick the driver's own 2x6
    // form from the preset list (a 6850 reports it as 53848 x 156125 microns,
    // i.e. 4x6 and 2x6 both carry bleed and neither is a round number).
    false,
    {
      orientation: 'portrait',
      pageSize: { width: 101600, height: 152400 },
      ...defaultMargins('default'),
      ...dpi(300),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'canon-g500',
    'Canon G500 series',
    ['canon g500', 'g500 series'],
    100,
    'medium',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 156100, height: 105000 },
      ...defaultMargins('default'),
      ...dpi(600),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'canon-g2020',
    'Canon G2020 series',
    ['canon g2020', 'g2020 series'],
    90,
    'low',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 101600, height: 152400 },
      ...defaultMargins('printableArea'),
      ...dpi(600),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'hiti-p510',
    'HiTi Photo Printer P510 series',
    ['hiti', 'p510'],
    90,
    'low',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 105500, height: 156500 },
      ...defaultMargins('default'),
      ...dpi(301),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'electron'
  ),
  rec(
    'epson-l3210',
    'EPSON L3210',
    ['l3210'],
    80,
    'low',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 100000, height: 150000 },
      ...defaultMargins('default'),
      ...dpi(300),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'sumatra'
  ),
  rec(
    'epson-l3110',
    'EPSON L3110 Series',
    ['l3110'],
    80,
    'low',
    true,
    {
      orientation: 'portrait',
      pageSize: { width: 101600, height: 152400 },
      ...defaultMargins('default'),
      ...dpi(720),
      scaleFactor: 100,
      printBackground: true,
      printResolutionScale: 1
    },
    'sumatra'
  )
]

function matcherHits(printerName: string, matcher: string): boolean {
  const raw = printerName.toLowerCase()
  const normalized = normalizePrinterName(printerName)
  const needle = matcher.toLowerCase()
  return raw.includes(needle) || normalized.includes(needle)
}

/** Return the best matching recommendation for a Windows printer queue name. */
export function findPrinterRecommendation(printerName: string): PrinterRecommendation | null {
  if (!printerName.trim()) return null

  const matches = PRINTER_RECOMMENDATIONS.filter((entry) =>
    entry.matchers.some((matcher) => matcherHits(printerName, matcher))
  )
  if (matches.length === 0) return null

  matches.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority
    const aLen = Math.max(...a.matchers.map((m) => m.length))
    const bLen = Math.max(...b.matchers.map((m) => m.length))
    return bLen - aLen
  })

  return matches[0] ?? null
}

/** True when settings still match the untouched app stock defaults (first-time setup). */
export function isStockAppPrintSettings(
  settings: PrintSettings,
  printMethod: PrintMethod = 'electron'
): boolean {
  if (printMethod !== 'electron') return false

  const orientationOk = settings.orientation === 'landscape'
  const widthOk = settings.pageSize.width === STOCK_PAGE_SIZE.width
  const heightOk = settings.pageSize.height === STOCK_PAGE_SIZE.height
  const scaleOk = (settings.scaleFactor ?? 100) === 100
  const resolutionOk = (settings.printResolutionScale ?? 1) === 1
  const marginType = settings.margins?.marginType
  const marginsOk = marginType === undefined || marginType === 'default'

  return orientationOk && widthOk && heightOk && scaleOk && resolutionOk && marginsOk
}

export function mergeRecommendationIntoSettings(
  base: PrintSettings,
  recommendation: PrinterRecommendation
): PrintSettings {
  const { settings } = recommendation
  return {
    ...base,
    orientation: settings.orientation,
    pageSize: { ...settings.pageSize },
    margins: settings.margins ? { ...settings.margins } : { marginType: 'default' },
    dpi: settings.dpi ? { ...settings.dpi } : base.dpi,
    scaleFactor: settings.scaleFactor ?? 100,
    printBackground: settings.printBackground ?? true,
    printResolutionScale: settings.printResolutionScale ?? 1
  }
}

export function getRecommendationMarginType(
  recommendation: PrinterRecommendation
): 'default' | 'none' | 'printableArea' | 'custom' {
  return recommendation.settings.margins?.marginType ?? 'default'
}

export function getRecommendationDpi(recommendation: PrinterRecommendation): {
  horizontal: number
  vertical: number
} {
  const h = recommendation.settings.dpi?.horizontal ?? 600
  const v = recommendation.settings.dpi?.vertical ?? h
  return { horizontal: h, vertical: v }
}

/** Page sizes within ~1% tolerance (microns). */
export function pageSizeApproxEqual(
  a: { width: number; height: number },
  b: { width: number; height: number },
  tolerancePct = 0.01
): boolean {
  const within = (x: number, y: number) => Math.abs(x - y) <= Math.max(x, y) * tolerancePct
  return within(a.width, b.width) && within(a.height, b.height)
}

export function presetMatchesRecommendation(
  preset: {
    orientation: 'landscape' | 'portrait'
    pageSize: { width: number; height: number }
  },
  recommendation: PrinterRecommendation
): boolean {
  return (
    preset.orientation === recommendation.settings.orientation &&
    pageSizeApproxEqual(preset.pageSize, recommendation.settings.pageSize)
  )
}

/**
 * Apply recommendation to the persisted print store when settings are still stock.
 * Used from SettingsModal on first printer pick.
 */
export function applyRecommendationToStoreIfStock(printerName: string): boolean {
  const recommendation = findPrinterRecommendation(printerName)
  if (!recommendation?.autoApply) return false

  const store = usePrintStore.getState()
  if (!isStockAppPrintSettings(store.settings, store.getPrintMethod('default'))) return false

  store.updateSettings(mergeRecommendationIntoSettings(store.settings, recommendation))
  store.setPrintMethod(recommendation.printMethod, 'default')
  return true
}
